import { schema } from 'normalizr';
import { createBatchActionsModule } from 'redux/reusable/batchSelections';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { actionCreatorFactory } from 'typescript-fsa';

// temporary flag to manage visibility
export const INTEGRATIONS_IS_ENABLED = false;

export interface IntegrationsState {
  xhrStatus: XhrStatus;
  entities: { integrations: { [id: number]: { id: number; name: string } } };
  result: Array<number>;
  selectedWorkspace: number | string | null;
  importAccountId: number | null;
  showUnassignedUsersModal: boolean;

  projects: {
    query: string;
    entities: { [id: string]: { id: number | string; name: string } };
    result: Array<number>;
  };
  import: {
    accountId: number | null;
    inProgress: boolean;
    importingFrom: Array<number>;
    importSuccessFrom: Array<number>;
    status: Array<ImportStatus>;
  };
  stepEntities: { [id: string]: { name: string; enabled: boolean; number: number } };
  steps: Array<string>;
  selection: {
    projectIds: Array<number>;
  };
}

export enum IntegrationTypes {
  PEOPLE = 'PEOPLE',
  PROJECTS = 'PROJECTS',
}
export type IntegrationTypesStrings = keyof typeof IntegrationTypes;

export interface ImportItemSummary {
  type: string;
  total: number;
  failed: number;
  imported: number;
  found_already_imported: number;
  messages: {
    warnings: Array<string>;
    errors: Array<string>;
  };
}

export interface ImportStatus {
  id: number | string;
  status: ImportStatuses;
  details: {
    config: { project_ids: Array<number>; workspace_id: number };
    summary: Array<ImportItemSummary>;
  } | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export enum CurrentImportStatus {
  created = 'created',
  in_progress = 'in_progress',
  finished = 'finished',
}

export type IntegrationWorkspaces = Array<{ id: number | string; name: string }>;
export type IntegrationProjects = Array<{ id: number | string; name: string }>;

export type ImportStatuses = keyof typeof CurrentImportStatus;

export const INTEGRATIONS_SCHEMA_KEY = 'integrations';
export const integrationsSchema = new schema.Entity(INTEGRATIONS_SCHEMA_KEY);
export const integrationsListSchema = [integrationsSchema];

export const WORKSPACE_PROJECTS_SCHEMA_KEY = 'workspace_projects';
export const workspaceProjectsSchema = new schema.Entity(WORKSPACE_PROJECTS_SCHEMA_KEY);
export const workspaceProjectsListSchema = [workspaceProjectsSchema];

const actionCreator = actionCreatorFactory('timely/integrations');

export const { batchSelectionActions, withBatchSelectionsReducer } =
  createBatchActionsModule<IntegrationsState['selection']>('integrations-selection');

export const fetchedIntegrationsAction =
  actionCreator<IntegrationWorkspaces>('INTEGRATIONS_FETCHED');
export const selectIntegrationWorkspace = actionCreator<{
  id: number | string | null;
}>('WORKSPACE_SELECTED');

export const fetchedProjectsForWorkspace = actionCreator<Array<any>>('PROJECTS_FETCHED');

export const selectImportAccountId = actionCreator<{ id: number | null; name: string }>(
  'IMPORT_ACCOUNT_ID_SELECTED'
);

export const submittedImportData = actionCreator<{ accountId: number }>('SUBMITTED_IMPORT_DATA');
export const fetchedImportStatus = actionCreator<{
  status: Array<ImportStatus>;
  accountId: number;
}>('FETCHED_IMPORT_STATUS');

export const enableNextStep = actionCreator<{ currentStep: string }>('ENABLE_NEXT_STEP');
export const disableNextStep = actionCreator<{ currentStep: string }>('DISABLE_NEXT_STEP');

export const searchProjects = actionCreator<string>('SEARCH');
export const resetProjectsSearch = actionCreator('RESET_SEARCH');

export const resetImportState = actionCreator('RESET_IMPORT_STATE');

export const toggleUnassignedUsersModal = actionCreator('TOGGLE_UNASSIGNED_USERS_MODAL');

export const setImportingFrom = actionCreator<{ accountId: number }>('SET_IMPORTING_FROM');
